import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import {
  getDailyQuotes,
  deleteDailyQuote,
  createDailyQuote,
  updateDailyQuote,
} from "../../../utils/api";

const columns = [
  {
    key: "date",
    label: "Date",
    render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
  },
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  {
    key: "quote",
    label: "Quote",
    render: (value) => (value ? String(value).slice(0, 120) : "-"),
  },
];

const filters = [
  {
    key: "author",
    label: "Filter by Author",
    type: "search",
  },
  {
    key: "date",
    label: "Filter by Date",
    type: "date",
  },
];

const initialFormData = {
  title: "",
  quote: "",
  shortDescription: "",
  author: "",
  date: new Date().toISOString().split("T")[0],
};

export default function DailyQuotes() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedQuote(null);
    setModalOpen(true);
  };

  const handleEdit = (quote) => {
    setSelectedQuote(quote);
    setFormData({
      title: quote.title || "",
      quote: quote.quote || "",
      shortDescription: quote.shortDescription || "",
      author: quote.author || "",
      date: quote.date ? new Date(quote.date).toISOString().split("T")[0] : "",
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (quote) => {
    handleEdit(quote);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode && selectedQuote) {
        await updateDailyQuote(selectedQuote._id, formData);
        alert("Daily quote updated successfully!");
      } else {
        await createDailyQuote(formData);
        alert("Daily quote created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save daily quote");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Daily Quotes"
          fetchData={getDailyQuotes}
          deleteData={deleteDailyQuote}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search daily quotes..."
        />
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Daily Quote" : "Add New Daily Quote"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Quote Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Author</label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleFormChange}
                  placeholder="Author name"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                placeholder="Optional title"
              />
            </div>

            <div className="form-group">
              <label>Quote *</label>
              <textarea
                name="quote"
                value={formData.quote}
                onChange={handleFormChange}
                rows="5"
                required
                placeholder="Enter the quote text"
              />
            </div>

            <div className="form-group">
              <label>Short Description</label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleFormChange}
                rows="3"
                placeholder="Optional short description"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEditMode ? "Update Quote" : "Create Quote"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
